import { useCallback, useEffect, useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import { AutoAwesome as AssistantIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSumator } from '../../contexts/SumatorContext';
import useRequest from '../../hooks/useRequest';
import {
    AI_SETTINGS_CHANGED_EVENT,
    type AiAssistantStatus,
    askAiButtonRight,
} from '../../domain/aiAssistant';
import AskAiWindow from './AskAiWindow';

/**
 * The floating Ask AI button, bottom-right on every page, and the chat window it opens.
 *
 * Shown to administrators only, and only once the assistant is switched on and has a provider
 * key (Configuration > Ask AI). It moves left of the Sumator while that is open, so the two
 * never cover each other. The Sumator's context is why this renders inside its provider.
 */
const AskAiButton = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { isOpen: sumatorOpen } = useSumator();
    const request = useRequest();
    const [status, setStatus] = useState<AiAssistantStatus | null>(null);
    const [open, setOpen] = useState(false);
    const isAdmin = user?.role === 'ADMIN';

    const loadStatus = useCallback(async () => {
        try {
            const response = await request<{ data: AiAssistantStatus }>({ path: '/ai-assistant/status' });
            setStatus(response.data);
        } catch {
            // Without a status the button stays hidden; nothing else depends on it.
            setStatus(null);
        }
    }, [request]);

    useEffect(() => {
        if (!isAdmin) {
            setStatus(null);
            setOpen(false);
            return undefined;
        }
        void loadStatus();
        const refresh = () => void loadStatus();
        window.addEventListener(AI_SETTINGS_CHANGED_EVENT, refresh);
        return () => window.removeEventListener(AI_SETTINGS_CHANGED_EVENT, refresh);
    }, [isAdmin, loadStatus]);

    if (!isAdmin || status === null || !status.enabled || status.models.length === 0) {
        return null;
    }

    const right = askAiButtonRight(sumatorOpen);
    const label = open ? t('aiAssistant.window.close') : t('aiAssistant.button.ariaLabel');

    return (
        <>
            <Tooltip title={open ? label : t('aiAssistant.button.tooltip')} placement="left">
                <Fab
                    color="secondary"
                    size="medium"
                    aria-label={label}
                    aria-expanded={open}
                    data-testid="ask-ai-button"
                    onClick={() => setOpen(previous => !previous)}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right,
                        zIndex: theme => theme.zIndex.drawer + 1,
                    }}
                >
                    {open ? <CloseIcon /> : <AssistantIcon />}
                </Fab>
            </Tooltip>
            <AskAiWindow status={status} open={open} right={right} onClose={() => setOpen(false)} />
        </>
    );
};

export default AskAiButton;
